import { GetStaticProps, GetStaticPaths } from 'next'
import { ReactElement } from 'react'

import { dehydrate, QueryClient } from 'react-query'

import { getHelpCenterCategorySlugs } from 'app/services/Api/helpCenter'
import { helpCenterConfig } from 'app/services/Query/help-center/help-center.query'
import { NextPage } from 'app/types/decalarations/next'
import { HELP_CENTER_CATEGORY_PARAMS } from 'app/utils/constants/help-center.constants'
import { isOfType } from 'app/utils/helpers/type.helpers'

import HelpCenterLayout from '~/components/layouts/HelpCenterLayout'
import MainLayout from '~/components/layouts/MainLayout'
import HelpCenterSubcategoryView from '~/views/HelpCenter/HelpCenterSubcategoryView'

const HelpCenterSubcategoryPage: NextPage = () => <HelpCenterSubcategoryView />

HelpCenterSubcategoryPage.getLayout = (page: ReactElement) => (
  <MainLayout>
    <HelpCenterLayout>{page}</HelpCenterLayout>
  </MainLayout>
)

export const getStaticPaths: GetStaticPaths = async () => {
  const { slugs } = await getHelpCenterCategorySlugs()

  const paths = slugs.flatMap(({ slug: categorySlug, sub_categories }) => {
    const categoryPath = { params: { category: categorySlug } }
    const subCategoryPaths =
      sub_categories?.map(({ slug }) => ({
        params: { category: slug }
      })) || []

    return [categoryPath, ...subCategoryPaths]
  })

  return { paths, fallback: true }
}

export const getStaticProps: GetStaticProps = async (context) => {
  try {
    const { category } = context?.params ?? {}
    const categorySlug = isOfType.string(category) ? category : ''

    const queryClient = new QueryClient()
    const { getHelpCenterCategories, getHelpCenterArticles } = helpCenterConfig
    const getHelpCenterCategoriesKey = getHelpCenterCategories.key(
      HELP_CENTER_CATEGORY_PARAMS
    )

    await queryClient.prefetchQuery(
      getHelpCenterCategoriesKey,
      getHelpCenterCategories.request
    )

    const categoriesState = queryClient.getQueryState<
      HelpCenterCategoryModel[]
    >(getHelpCenterCategoriesKey)

    const categories = categoriesState?.data

    const currentCategory = categories?.find((el) => el.slug === categorySlug)
    const currentSubcategory = categories?.find((el) =>
      el.sub_categories?.find((el) => el.slug === categorySlug)
    )

    if (!currentCategory?.id && !currentSubcategory?.id) {
      throw new Error('Category not found')
    }

    await queryClient.prefetchQuery(
      getHelpCenterArticles.key({ byCategory: categorySlug }),
      getHelpCenterArticles.request
    )

    return {
      props: { dehydratedState: dehydrate(queryClient) }
    }
  } catch (error) {
    return { notFound: true }
  }
}

export default HelpCenterSubcategoryPage
